import { NoteKind, NoteRole } from './notes.types';

/**
 * Who a note is by, and who it is for.
 *
 * The role codes are the database's (`adm`/`cgv`/`doc`/`clt`), and the words they
 * render as are the ones this app already uses for those people: `caregiver`,
 * `doctor` and `client` are existing keys carrying "Caregiver"/"Νοσηλευτής",
 * "Doctor"/"Γιατρός" and "Patient"/"Ασθενής". Only `adm` needed a key of its own —
 * the app names the office in several places but had no single word for it.
 */
export const NOTE_ROLE_LABEL_KEYS: Record<NoteRole, string> = {
  adm: 'notes-role-adm',
  cgv: 'caregiver',
  doc: 'doctor',
  clt: 'client',
};

/**
 * The office is in every audience, and is not a choice.
 *
 * The note is the office's own, and the row-level policy reads `audience` in both
 * directions: an audience without the author's role is a note the author cannot read
 * back, which the write is refused for.
 */
export const OFFICE_ROLE: NoteRole = 'adm';

/**
 * A recipient group the office can send a note to, on top of the office itself.
 *
 * The patient (`clt`) is deliberately not one of them. The column would take it and
 * the server would accept it, but nothing client-facing reads notes yet, so a note
 * marked client-readable today would become visible to the patient the day a portal
 * ships, with no one having chosen that.
 */
export type AudienceGroup = Extract<NoteRole, 'doc' | 'cgv'>;

export type AudienceOption = {
  role: AudienceGroup;
  labelKey: string;
  hintKey: string;
};

export const AUDIENCE_OPTIONS: readonly AudienceOption[] = [
  { role: 'doc', labelKey: 'notes-audience-doctor', hintKey: 'notes-audience-doctor-hint' },
  { role: 'cgv', labelKey: 'notes-audience-care-team', hintKey: 'notes-audience-care-team-hint' },
];

/** The chosen groups as the audience to write: the office first, then them, in a stable order. */
export const buildAudience = (groups: readonly AudienceGroup[]): NoteRole[] => [
  OFFICE_ROLE,
  ...AUDIENCE_OPTIONS.filter((option) => groups.includes(option.role)).map((option) => option.role),
];

/**
 * The audience a DOCTOR's note goes to: the office, and the doctor themselves.
 *
 * "Only the office" cannot be written as `['adm']`. The insert policy refuses a note
 * whose author is not in its own audience - a note the author could not read back -
 * so a doctor addressing the office alone is rejected outright, which is what the
 * composer's "could not be saved" was.
 *
 * `doc` in the audience does not widen who is TOLD. Recipients are the audience minus
 * the author's own role, so this notifies the office and nobody else; what it buys is
 * that the doctor can still see what they sent. Caregivers are never in it.
 */
export const buildDoctorAudience = (): NoteRole[] => [OFFICE_ROLE, 'doc'];

/**
 * Who a note already written was sent to: its audience, less the author's own role.
 *
 * The author is in the audience of everything they write — the row-level policy
 * refuses a note its own author could not read back — so their role is never a
 * recipient. Stripping the office instead would caption a caregiver's note to the
 * office as having been sent to the caregiver, which is the note's author.
 */
export const recipientsBesidesAuthor = (audience: readonly NoteRole[], authorRole: NoteRole): NoteRole[] =>
  audience.filter((role) => role !== authorRole);

/**
 * A note's kind follows from its audience rather than being a second question.
 *
 * A note that reaches someone outside the office is addressed to them and notifies
 * them, which is what MESSAGE means; one that does not is a record, which is
 * VISIT_NOTE. This is the only place that decision is made.
 */
export const kindForAudience = (audience: readonly NoteRole[]): NoteKind =>
  // The composer writes as the office, so the office is the author here.
  recipientsBesidesAuthor(audience, OFFICE_ROLE).length > 0 ? 'MESSAGE' : 'VISIT_NOTE';
